import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import api from "../../utils/api";
import { useToast } from "contexts/ToastContext";
import { brand, radius } from "utils/designTokens";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Stack,
  Paper
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DirhamSymbol from "components/currency/DirhamSymbol";
import CurrencyInputAdornment from "components/currency/CurrencyInputAdornment";

import {
  emptyCouponForm,
  validateCouponForm,
  buildCouponPayload,
  buildFormFromCoupon,
  cleanCouponCode
} from "./couponUtils";

dayjs.extend(customParseFormat);

const offerCancelButtonSx = {
  borderRadius: `${radius.sm}px`,
  px: 2.5,
  textTransform: "none",
  fontWeight: 600,
  color: "text.secondary",
  borderColor: "divider",
  "&:hover": {
    borderColor: brand.borderHover,
    bgcolor: brand.overlay
  }
};

const offerPrimaryButtonSx = {
  borderRadius: `${radius.sm}px`,
  px: 2.5,
  textTransform: "none",
  fontWeight: 600,
  bgcolor: brand.primary,
  boxShadow: "none",
  "&:hover": {
    bgcolor: brand.primaryDark,
    boxShadow: "none"
  }
};

export default function CouponFormDialog({ open, couponId, initialCoupon, onClose, onSaved }) {
  const { showToast, showApiError } = useToast();
  const [form, setForm] = useState(emptyCouponForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [apiError, setApiError] = useState("");
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const isEdit = Boolean(couponId);

  useEffect(() => {
    if (!open) return;
    setLoadingStores(true);
    api
      .get("/store/list")
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setStores(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load stores for coupon form:", err);
        setStores([]);
      })
      .finally(() => {
        setLoadingStores(false);
      });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm(emptyCouponForm());
      setErrors({});
      setApiError("");
      setSaving(false);
      setLoadingDetails(false);
      return;
    }

    setErrors({});
    setApiError("");
    setSaving(false);

    if (isEdit) {
      if (initialCoupon) {
        setForm(buildFormFromCoupon(initialCoupon));
      } else {
        setLoadingDetails(true);
        api
          .get(`/offer/${couponId}`)
          .then((res) => {
            const data = res.data?.data || res.data;
            setForm(buildFormFromCoupon(data));
          })
          .catch((err) => {
            showApiError(err, "Failed to load coupon details");
            onClose();
          })
          .finally(() => {
            setLoadingDetails(false);
          });
      }
    } else {
      setForm(emptyCouponForm());
    }
  }, [open, couponId, initialCoupon, isEdit, showApiError, onClose]);

  const handleChange = (field, value) => {
    let val = value;
    if (field === "coupon_code") {
      val = cleanCouponCode(value);
    }
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (apiError) setApiError("");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (saving) return;

    const validationErrors = validateCouponForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const payload = buildCouponPayload(form);

      if (isEdit) {
        await api.put(`/offer/edit/${couponId}`, payload);
        showToast("Coupon updated successfully", { severity: "success" });
      } else {
        await api.post("/offer/create", payload);
        showToast("Coupon created successfully", { severity: "success" });
      }

      onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to save coupon";
      setApiError(msg);
      showApiError(err, msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {isEdit ? "Edit Coupon" : "Create New Coupon"}
        <Typography variant="body2" color="text.secondary">
          {isEdit ? "Update promotional coupon details." : "Add a new promotional coupon code."}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 2.5 }}>
        {loadingDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box component="form" noValidate onSubmit={handleSubmit}>
              {apiError && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                  {apiError}
                </Alert>
              )}

              <Grid container spacing={2.5}>
                {/* Store Selection */}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Store"
                    value={form.store_id}
                    onChange={(e) => handleChange("store_id", e.target.value)}
                    error={Boolean(errors.store_id)}
                    helperText={errors.store_id || (loadingStores ? "Loading available stores..." : "Select the store this coupon applies to.")}
                    disabled={loadingStores}
                    InputProps={
                      loadingStores
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CircularProgress size={16} />
                              </InputAdornment>
                            )
                          }
                        : undefined
                    }
                  >
                    <MenuItem value="" disabled>
                      <em>Select Store</em>
                    </MenuItem>
                    {stores.map((s) => {
                      const sId = s.store_id ?? s.id ?? s.storeId;
                      const sName = s.title_en || s.name || s.store_name || s.title_ar || sId;
                      return (
                        <MenuItem key={sId} value={sId}>
                          {sName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                {/* Coupon Code & Discount Type */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Coupon Code"
                    placeholder="e.g. SUMMER20"
                    value={form.coupon_code}
                    onChange={(e) => handleChange("coupon_code", e.target.value)}
                    error={Boolean(errors.coupon_code)}
                    helperText={errors.coupon_code || "Only letters, numbers, hyphens & underscores."}
                    inputProps={{ style: { textTransform: "uppercase", fontWeight: 700 } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Discount Type"
                    value={form.offer_value_type}
                    onChange={(e) => handleChange("offer_value_type", e.target.value)}
                  >
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="fixed">
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        Fixed Amount (<DirhamSymbol size={13} />)
                      </Box>
                    </MenuItem>
                  </TextField>
                </Grid>

                {/* Offer Name EN & AR */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Offer Name (English)"
                    placeholder="e.g. Summer Special 20% Off"
                    value={form.offer_name_en}
                    onChange={(e) => handleChange("offer_name_en", e.target.value)}
                    error={Boolean(errors.offer_name_en)}
                    helperText={errors.offer_name_en}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Offer Name (Arabic)"
                    placeholder="خصم الصيف 20%"
                    value={form.offer_name_ar}
                    onChange={(e) => handleChange("offer_name_ar", e.target.value)}
                    inputProps={{ dir: "rtl" }}
                  />
                </Grid>

                {/* Discount Value & Max Discount */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label={form.offer_value_type === "percentage" ? "Discount Percentage (%)" : "Discount Value"}
                    placeholder={form.offer_value_type === "percentage" ? "10" : "50"}
                    value={form.offer_value}
                    onChange={(e) => handleChange("offer_value", e.target.value)}
                    error={Boolean(errors.offer_value)}
                    helperText={errors.offer_value}
                    InputProps={{
                      startAdornment: form.offer_value_type === "fixed" ? <CurrencyInputAdornment /> : undefined,
                      endAdornment:
                        form.offer_value_type === "percentage" ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : undefined
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Purchase Amount"
                    placeholder="0"
                    value={form.minimum_purchase_amount}
                    onChange={(e) => handleChange("minimum_purchase_amount", e.target.value)}
                    error={Boolean(errors.minimum_purchase_amount)}
                    helperText={errors.minimum_purchase_amount || "Minimum order total required."}
                    InputProps={{
                      startAdornment: <CurrencyInputAdornment />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Discount Amount"
                    placeholder="Optional limit"
                    value={form.max_discount_amount}
                    onChange={(e) => handleChange("max_discount_amount", e.target.value)}
                    disabled={form.offer_value_type === "fixed"}
                    error={Boolean(errors.max_discount_amount)}
                    helperText={errors.max_discount_amount || (form.offer_value_type === "percentage" ? "Cap for percentage discount." : "N/A for fixed discount")}
                    InputProps={{
                      startAdornment: <CurrencyInputAdornment />
                    }}
                  />
                </Grid>

                {/* Date & Time Range */}
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Start Date & Time"
                    value={form.from_datetime}
                    onChange={(val) => handleChange("from_datetime", val)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(errors.from_datetime),
                        helperText: errors.from_datetime
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="End Date & Time"
                    value={form.to_datetime}
                    onChange={(val) => handleChange("to_datetime", val)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(errors.to_datetime),
                        helperText: errors.to_datetime
                      }
                    }}
                  />
                </Grid>

                {/* Description EN & AR */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description (English)"
                    placeholder="Short coupon description shown to customer."
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description (Arabic)"
                    placeholder="وصف الكوبون بالعربية"
                    value={form.description_ar}
                    onChange={(e) => handleChange("description_ar", e.target.value)}
                    inputProps={{ dir: "rtl" }}
                  />
                </Grid>

                {/* Options / Switches */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.is_first_order}
                              onChange={(e) => handleChange("is_first_order", e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                First Order Only
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Restrict coupon to new customer first orders only
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.status}
                              onChange={(e) => handleChange("status", e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                Active Status
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Enable or disable coupon availability
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </LocalizationProvider>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={offerCancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loadingDetails}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          sx={offerPrimaryButtonSx}
        >
          {saving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Update Coupon" : "Create Coupon"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CouponFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  couponId: PropTypes.string,
  initialCoupon: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};
